import { defineMessage, type MessageDescriptor } from 'react-intl';

type CreateKbMessages = {
	titleMessage: MessageDescriptor;
	ctaMessage: MessageDescriptor;
	headerImageAltMessage: MessageDescriptor;
	nameLabelMessage: MessageDescriptor;
	typeLabelMessage: MessageDescriptor;
	selectSpaceLabelMessage: MessageDescriptor;
	typeTooltipMessage: MessageDescriptor;
	typeInfoIconLabelMessage: MessageDescriptor;
	typeExternalMessage: MessageDescriptor;
	typeInternalMessage: MessageDescriptor;
	confirmCtaMessage: MessageDescriptor;
	backCtaMessage: MessageDescriptor;
	spaceNamePlaceholderMessage: MessageDescriptor;
	saveForLaterMessage: MessageDescriptor;
	publishAllMessage: MessageDescriptor;
	publishCtaMessage: MessageDescriptor;
	createSpaceMessage: MessageDescriptor;
	createSpaceDisabledTooltipMessage: MessageDescriptor;
	publishFormDescriptionMessage: MessageDescriptor;
	publishFormLinkingErrorMessage: MessageDescriptor;
	genericActionErrorMessage: MessageDescriptor;
	publishToNewSpaceCheckboxLabelMessage: MessageDescriptor;
};

const titleMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.title',
	defaultMessage: 'Create knowledge base',
	description: 'Header title for Create KB action card',
});

const ctaMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.cta',
	defaultMessage: 'Create drafts',
	description: 'Primary action button label in Create KB card',
});

const headerImageAltMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.headerImageAlt',
	defaultMessage: 'Knowledge base article preview',
	description: 'Alt text for create KB card image',
});

const nameLabelMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.nameLabel',
	defaultMessage: 'Name your space',
	description: 'Label for the knowledge base space name input',
});

const typeLabelMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.typeLabel',
	defaultMessage: 'Type',
	description: 'Label for the knowledge base type select',
});

const selectSpaceLabelMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.selectSpaceLabel',
	defaultMessage: 'Select space',
	description: 'Label for the selected destination space in publish form',
});

const typeTooltipMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.typeTooltip',
	defaultMessage:
		'External spaces are visible to customers. Internal spaces are visible to agents only.',
	description: 'Tooltip text explaining the knowledge base type field',
});

const typeInfoIconLabelMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.typeInfoIconLabel',
	defaultMessage: 'More information about knowledge base type',
	description: 'Accessible label for the info icon next to the type field',
});

const typeExternalMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.typeExternal',
	defaultMessage: 'External',
	description: 'Option label for an external knowledge base type',
});

const typeInternalMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.typeInternal',
	defaultMessage: 'Internal',
	description: 'Option label for an internal knowledge base type',
});

const confirmCtaMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.confirmCta',
	defaultMessage: 'Confirm',
	description: 'Primary action button label to confirm knowledge base creation',
});

const backCtaMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.backCta',
	defaultMessage: 'Back',
	description: 'Subtle action button label to go back to the initial Create KB card state',
});

const spaceNamePlaceholderMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.spaceNamePlaceholder',
	defaultMessage: 'IT service desk',
	description: 'Placeholder for the knowledge base space name input',
});

const saveForLaterMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.saveForLater',
	defaultMessage: 'Save for later',
	description: 'Subtle action button label to save knowledge base draft for later',
});

const publishAllMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.publishAll',
	defaultMessage: 'Publish all',
	description: 'Primary action button label to publish all knowledge base drafts',
});

const publishCtaMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.publishCta',
	defaultMessage: 'Publish',
	description: 'Primary action button label to publish the selected knowledge base',
});

const createSpaceMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.createSpace',
	defaultMessage: 'Create space',
	description: 'Inline action label to create a new Confluence space from publish form',
});

const createSpaceDisabledTooltipMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.createSpaceDisabledTooltip',
	defaultMessage: "You don't have permission to create spaces in Confluence.",
	description: 'Tooltip and accessible description for a disabled create-space action',
});

const publishFormDescriptionMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.publishFormDescription',
	defaultMessage:
		'All your articles will be visible when you link your space and create knowledge base.',
	description: 'Informational description shown in the publish form',
});

const publishFormLinkingErrorMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.publishFormLinkingError',
	defaultMessage:
		"Articles can't be published because your knowledge base already has content. You can add or edit articles directly from your knowledge base.",
	description: 'Error shown when linking a space fails because knowledge base already has content',
});

const genericActionErrorMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.genericActionError',
	defaultMessage: 'Something went wrong. Try again.',
	description:
		'Generic error shown when creating drafts or publishing fails for reasons other than KB already having content',
});

const publishToNewSpaceCheckboxLabelMessage: MessageDescriptor = defineMessage({
	id: 'conversation-assistant-jsm-create-kb-card.controllers.create-kb.publishToNewSpaceCheckboxLabel',
	defaultMessage:
		'By continuing to publish, you agree to let us add Confluence app on your site. It appears on your bill with zero cost.',
	description: 'Checkbox label shown after confirming create-space inline edit in publish form',
});

export const createKbMessages: CreateKbMessages = {
	titleMessage: titleMessage,
	ctaMessage: ctaMessage,
	headerImageAltMessage: headerImageAltMessage,
	nameLabelMessage: nameLabelMessage,
	typeLabelMessage: typeLabelMessage,
	selectSpaceLabelMessage: selectSpaceLabelMessage,
	typeTooltipMessage: typeTooltipMessage,
	typeInfoIconLabelMessage: typeInfoIconLabelMessage,
	typeExternalMessage: typeExternalMessage,
	typeInternalMessage: typeInternalMessage,
	confirmCtaMessage: confirmCtaMessage,
	backCtaMessage: backCtaMessage,
	spaceNamePlaceholderMessage: spaceNamePlaceholderMessage,
	saveForLaterMessage: saveForLaterMessage,
	publishAllMessage: publishAllMessage,
	publishCtaMessage: publishCtaMessage,
	createSpaceMessage: createSpaceMessage,
	createSpaceDisabledTooltipMessage: createSpaceDisabledTooltipMessage,
	publishFormDescriptionMessage: publishFormDescriptionMessage,
	publishFormLinkingErrorMessage: publishFormLinkingErrorMessage,
	genericActionErrorMessage: genericActionErrorMessage,
	publishToNewSpaceCheckboxLabelMessage: publishToNewSpaceCheckboxLabelMessage,
} as const;
